import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, MapPin, Calendar, BookOpen, Camera, Mic, Clock, Heart, ChefHat, Lightbulb, PenLine, Mail, Edit } from 'lucide-react'

/**
 * MemberDetailPanel - Slide-in panel showing full member profile
 *
 * Opens from the right side when a member card is clicked.
 * Contains: Hero, About, Stats, Wisdom Topics, Recent Contributions, Connection Score, Actions
 */
function MemberDetailPanel({ isOpen, onClose, member, isChild = false }) {
  const navigate = useNavigate()

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!member) return null

  // Calculate connection score (0-5 hearts based on activity)
  const getConnectionScore = () => {
    const storyCount = member.story_count || 0
    const photoCount = member.photo_count || 0
    const voiceCount = member.voice_count || 0
    const total = storyCount + photoCount + voiceCount

    if (!member.has_account && !member.invitation_accepted) return { hearts: 0, label: 'Not yet connected', description: "Hasn't joined yet" }
    if (total === 0) return { hearts: 1, label: 'Getting started', description: 'Just joined, encourage sharing' }
    if (total <= 2) return { hearts: 2, label: 'Getting started', description: 'Encourage more sharing' }
    if (total <= 5) return { hearts: 3, label: 'Growing', description: 'Building family connections' }
    if (total <= 10) return { hearts: 4, label: 'Strong', description: 'Active contributor' }
    return { hearts: 5, label: 'Very Strong', description: 'Top contributor!' }
  }

  const connectionScore = getConnectionScore()

  // Get avatar color based on relationship/gender
  const getAvatarColor = () => {
    if (member.relationship === 'You') return 'bg-[#556B2F]'
    if (member.relationship === 'Spouse') return 'bg-purple-500'
    if (member.gender === 'female') return 'bg-pink-400'
    if (member.gender === 'male') return 'bg-blue-400'
    return 'bg-gray-400'
  }

  // Format join date
  const formatJoinDate = (dateStr) => {
    if (!dateStr) return 'Recently'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  // Render hearts for connection score
  const renderHearts = (count) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Heart
            key={i}
            className={`w-5 h-5 ${i <= count ? 'text-green-500 fill-green-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    )
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
          isOpen ? 'opacity-35' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[520px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Close Bar */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <span className="text-lg font-semibold text-gray-700">Member Profile</span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close panel"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 1. Hero Section */}
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full ${getAvatarColor()} flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4`}>
              {isChild ? '👶' : (member.first_name?.[0] || 'U')}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 font-serif">{member.name}</h2>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mt-2 ${
              member.relationship === 'You' ? 'bg-[#556B2F] text-white' :
              member.relationship === 'Spouse' ? 'bg-purple-100 text-purple-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {member.relationship || 'Family Member'}
            </span>

            {/* Location */}
            {member.location && (
              <div className="flex items-center justify-center gap-1 mt-3 text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>{member.location}</span>
              </div>
            )}

            {/* Join Date */}
            <div className="flex items-center justify-center gap-1 mt-2 text-gray-500 text-sm">
              <Calendar className="w-4 h-4" />
              <span>Joined {formatJoinDate(member.joined_at)}</span>
            </div>
          </div>

          {/* 2. About Section */}
          {member.bio && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">About</h3>
              <p className="text-gray-700 leading-relaxed">{member.bio}</p>
            </div>
          )}

          {/* 3. Engagement Stats */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {isChild ? 'Dedicated to Them' : 'Contributions'}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <BookOpen className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-gray-900">{member.story_count || 0}</div>
                <div className="text-sm text-gray-600">{isChild ? 'Stories about them' : 'Stories'}</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <Camera className="w-6 h-6 text-green-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-gray-900">{member.photo_count || 0}</div>
                <div className="text-sm text-gray-600">{isChild ? 'Photos of them' : 'Photos'}</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                {isChild ? (
                  <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                ) : (
                  <ChefHat className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                )}
                <div className="text-2xl font-bold text-gray-900">{member.recipe_count || member.time_capsules || 0}</div>
                <div className="text-sm text-gray-600">{isChild ? 'Time Capsules' : 'Recipes'}</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <Mic className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                <div className="text-2xl font-bold text-gray-900">{member.voice_count || 0}</div>
                <div className="text-sm text-gray-600">{isChild ? 'Voice messages' : 'Recordings'}</div>
              </div>
            </div>
          </div>

          {/* 4. Wisdom Topics (adults only) */}
          {!isChild && member.wisdom_topics && member.wisdom_topics.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Wisdom Topics</h3>
              <div className="flex flex-wrap gap-2">
                {member.wisdom_topics.map((topic, i) => (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-sm ${
                      topic.isPrimary
                        ? 'bg-[#6B8E23] text-white'
                        : 'border border-[#6B8E23] text-[#6B8E23]'
                    }`}
                  >
                    {topic.text || topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 5. Recent Contributions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent Activity</h3>
            {member.recent_contributions && member.recent_contributions.length > 0 ? (
              <div className="space-y-3">
                {member.recent_contributions.map((contrib, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                      contrib.type === 'story' ? 'bg-blue-100' :
                      contrib.type === 'photo' ? 'bg-green-100' :
                      contrib.type === 'recipe' ? 'bg-amber-100' :
                      'bg-purple-100'
                    }`}>
                      {contrib.icon || '📖'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{contrib.title}</p>
                      <p className="text-sm text-gray-500">{contrib.meta}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-xl">
                <p className="text-gray-500">No contributions yet.</p>
                {!member.has_account && (
                  <p className="text-sm text-gray-400 mt-1">Send an invitation reminder!</p>
                )}
              </div>
            )}
          </div>

          {/* 6. Family Connection Score */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Family Connection</h3>
            <div className="flex items-center gap-4">
              {renderHearts(connectionScore.hearts)}
              <div>
                <div className="font-semibold text-gray-900">{connectionScore.label}</div>
                <div className="text-sm text-gray-600">{connectionScore.description}</div>
              </div>
            </div>
          </div>

          {/* 7. Action Buttons */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            {isChild ? (
              <>
                <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#556B2F] text-white rounded-xl font-semibold hover:bg-[#4a5f29] transition-colors">
                  <BookOpen className="w-5 h-5" />
                  Write a Story About {member.first_name}
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#E65100] text-white rounded-xl font-semibold hover:bg-[#D84315] transition-colors">
                  <Clock className="w-5 h-5" />
                  Create a Time Capsule for {member.first_name}
                </button>
              </>
            ) : member.has_account || member.invitation_accepted ? (
              <>
                <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#556B2F] text-white rounded-xl font-semibold hover:bg-[#4a5f29] transition-colors">
                  <Lightbulb className="w-5 h-5" />
                  Ask for Wisdom
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#E65100] text-white rounded-xl font-semibold hover:bg-[#D84315] transition-colors">
                  <PenLine className="w-5 h-5" />
                  Send a Story Prompt
                </button>
              </>
            ) : (
              <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#E65100] text-white rounded-xl font-semibold hover:bg-[#D84315] transition-colors">
                <Mail className="w-5 h-5" />
                Resend Invitation
              </button>
            )}
            <button
              onClick={() => {
                onClose()
                navigate(`/family-hub/member/${member.id}`)
              }}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              <Edit className="w-5 h-5" />
              Edit Profile
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default MemberDetailPanel
